import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';

import { APP_ENVIRONMENT } from '@core/tokens/environment.token';
import type { AuthSession, Credentials } from '@domain/models/auth.model';
import { AuthRepository } from '@domain/repositories/auth.repository';

import type { AuthSessionDto, EmployeeSignInResponseDto } from '@data/dto/api.dto';
import { mapAuthSessionDto, mapEmployeeSignInResponse } from '@data/mappers/user.mapper';

@Injectable()
export class AuthApiRepository extends AuthRepository {
  private readonly http = inject(HttpClient);
  private readonly env = inject(APP_ENVIRONMENT);
  private readonly signInPath = 'api/Employees/sign-in';

  login(credentials: Credentials): Observable<AuthSession> {
    return this.http
      .post<EmployeeSignInResponseDto>(this.signInPath, {
        email: credentials.email.trim(),
        password: credentials.password,
      })
      .pipe(
        map((response) => mapEmployeeSignInResponse(response, credentials.email.trim())),
        tap((session) => this.persistSession(session)),
      );
  }

  logout(): Observable<void> {
    this.clearSession();
    return of(void 0);
  }

  restoreSession(): AuthSession | null {
    const raw = localStorage.getItem(this.env.tokenStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as AuthSessionDto;
      return mapAuthSessionDto(parsed);
    } catch {
      this.clearSession();
      return null;
    }
  }

  persistSession(session: AuthSession): void {
    const dto: AuthSessionDto = {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt.toISOString(),
      user: {
        id: session.user.id,
        fullName: session.user.fullName,
        email: session.user.email,
        roles: [...session.user.roles],
      },
    };

    localStorage.setItem(this.env.tokenStorageKey, JSON.stringify(dto));
  }

  clearSession(): void {
    localStorage.removeItem(this.env.tokenStorageKey);
  }
}
